/**
 * Capitalize string
 */
export const capitalize = (str?: string): string => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

/**
 * Convert string to title case
 */
export const toTitleCase = (str?: string): string =>
  str ? str.toLowerCase().split(' ').map(capitalize).join(' ') : '';

/**
 * Convert string to sentence case
 */
export const toSentenceCase = (str: string) => str && str[0].toUpperCase() + str.slice(1).toLowerCase();

/**
 * Convert string to kebab-case
 */
export const toKebabCase = (str?: string): string => {
  if (!str) {
    return '';
  }

  const matchedText = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z][0-9]+/g);

  return matchedText ? matchedText.map((x) => x.toLowerCase()).join('-') : '';
};

/**
 * Concat array of values to comma-separated string
 *
 * Truncate to maxChar and append ', + <remaining values>'
 * "Cat, dog, goldfish, +4"
 */
export const truncateWithRemainder = (list: (string | undefined)[], maxChar: number = 30): string => {
  if (!list.length) {
    return '';
  }

  let s = list[0];
  let r = 0;

  for (let i = 1; i < list.length && s && s.length < maxChar; i++) {
    const next = s + ', ' + list[i];
    if (next.length < maxChar) {
      s = next;
    } else {
      r = list.length - i;
      break;
    }
  }
  return s + (r ? `, +${r}` : '');
};

export { bytesToSize } from './bytes';

export const secondsToMinutes = (seconds: number) => Math.round(seconds / 60);
export const minutesToSeconds = (minutes: number) => Math.round(minutes * 60);
export const secondsToHours = (seconds: number) => Math.round(seconds / 60 / 60);
export const hoursToSeconds = (hours: number) => Math.round(hours * 60 * 60);

const getHMSFromTime = (time: number, unitsPerDay: number, multiplier: number, substringTo: number) => {
  const roundedTime = Math.round(time);
  const HOURS_PER_DAY = 24;
  const days = Math.floor(roundedTime / unitsPerDay);
  const remainderTime = roundedTime % unitsPerDay;
  const hms = new Date(remainderTime * multiplier).toISOString().substring(11, substringTo);
  return hms.replace(/^(\d+)/, (h) => `${Number(h) + days * HOURS_PER_DAY}`.padStart(2, '0'));
};

/**
 * Convert seconds to hh:mm:ss format
 */
export const secondsToHMS = (seconds: number) => getHMSFromTime(seconds, 86400, 1000, 19);

/**
 * Convert milliseconds to hh:mm:ss.mm format
 */
export const millisecondsToHMSM = (milliseconds: number) => getHMSFromTime(milliseconds, 86400000, 1, 23);

/**
 * Convert hh:mm:ss(.mm) to milliseconds
 */
export const HMSMtoMs = (timeString?: string) => {
  if (!timeString) {
    return 0;
  }

  const [h, m, sMs] = timeString.split(':');
  const [s, ms] = sMs.split('.');

  const [fH, fM, fS, fMs] = [h, m, s, ms].filter(Boolean).map((str) => Number.parseInt(str, 10));

  const time = (fH * 3600 + fM * 60 + fS) * 1000 + (fMs || 0);

  return Math.round(time);
};

/**
 * Convert large numbers into a more friendly format, e.g. 1500000 -> 1.5M
 */
export const humanReadableNumber = (num?: number | string | null) => {
  if (typeof num === 'undefined' || num === null) {
    return '--';
  }
  return new Intl.NumberFormat('en-US', {
    notation: Number(num) >= 100_000 ? 'compact' : 'standard',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(Number(num));
};

export const getCommaSeparatedString = (items: string[]) =>
  items.reduce((acc, curr, i) => (i !== 0 ? `${acc} ${items.length - 1 === i ? ' and  ' : ', '}  ${curr}` : curr), '');

export const splitByNewLines = (value: string) =>
  value
    .split(/\s*\n\s*/)
    .map((v) => v.trim())
    .filter((v) => v !== '');
